import React, { useState, useRef, useEffect, useCallback } from "react";
import { Box, Typography, IconButton, TextField, Avatar, Fade } from "@mui/material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { API_BASE, demo, glassCard } from "./demoStyles";
import MarkdownLite from "./MarkdownLite";

// One chat session per page load
const makeSessionId = () =>
  `web-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const TypingDots = () => (
  <Box sx={{ display: "flex", gap: "4px", alignItems: "center", py: "4px" }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          backgroundColor: demo.textDim,
          animation: "demoBlink 1.2s infinite ease-in-out",
          animationDelay: `${i * 0.18}s`,
          "@keyframes demoBlink": {
            "0%, 80%, 100%": { opacity: 0.25, transform: "translateY(0)" },
            "40%": { opacity: 1, transform: "translateY(-3px)" },
          },
        }}
      />
    ))}
  </Box>
);

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Namaste! I'm ServoSaathi, your Agentic AI assistant for renewable energy. Ask me to show device status, control a pump, filter your fleet, or troubleshoot a fault — in any language.",
    },
  ]);
  const sessionId = useRef(makeSessionId());
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || busy) return;

    setInput("");
    setBusy(true);
    setMessages((prev) => [
      ...prev,
      { role: "user", text },
      { role: "assistant", text: "", streaming: true },
    ]);

    const appendToLast = (chunk) =>
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        next[next.length - 1] = { ...last, text: last.text + chunk };
        return next;
      });

    const finishLast = () =>
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        next[next.length - 1] = { ...last, streaming: false };
        return next;
      });

    try {
      const res = await fetch(`${API_BASE}/demo/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId.current }),
      });

      if (!res.ok || !res.body) {
        throw new Error("Assistant is unavailable right now.");
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split("\n");
        buffer = lines.pop(); // keep the last (possibly incomplete) line

        for (const raw of lines) {
          const line = raw.replace(/\r$/, "");
          if (!line.startsWith("data:")) continue;
          let data = line.slice(5);
          if (data.startsWith(" ")) data = data.slice(1); // SSE: strip one space
          if (data === "[DONE]") {
            finishLast();
            continue;
          }
          if (data.startsWith("[ERROR]")) {
            appendToLast("\n⚠️ Something went wrong. Please try again.");
            continue;
          }
          appendToLast(data);
        }
      }
      finishLast();
    } catch (err) {
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        next[next.length - 1] = {
          role: "assistant",
          text: last.text || "⚠️ I couldn't reach the assistant. Please try again.",
          streaming: false,
        };
        return next;
      });
    } finally {
      setBusy(false);
    }
  }, [input, busy]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Launcher */}
      <Fade in={!open}>
        <IconButton
          onClick={() => setOpen(true)}
          sx={{
            position: "fixed",
            bottom: 28,
            right: 28,
            zIndex: 1300,
            width: 60,
            height: 60,
            color: "#fff",
            background: "linear-gradient(135deg, #7c3aed, #a78bfa)",
            boxShadow: "0 10px 30px rgba(124,58,237,0.45)",
            animation: "launcherPulse 3s ease-in-out infinite",
            "@keyframes launcherPulse": {
              "0%,100%": { boxShadow: "0 10px 30px rgba(124,58,237,0.45)" },
              "50%":     { boxShadow: "0 10px 40px rgba(167,139,250,0.65)" },
            },
            "&:hover": {
              background: "linear-gradient(135deg, #6d28d9, #7c3aed)",
              transform: "translateY(-2px)",
            },
            transition: "transform 0.2s ease",
          }}
        >
          <SmartToyOutlinedIcon />
        </IconButton>
      </Fade>

      {/* Panel */}
      <Fade in={open}>
        <Box
          sx={{
            position: "fixed",
            bottom: 28,
            right: 28,
            zIndex: 1301,
            width: { xs: "calc(100vw - 32px)", sm: 392 },
            height: 560,
            maxHeight: "calc(100vh - 56px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            ...glassCard,
            background: "rgba(12,17,28,0.92)",
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
            boxShadow: "0 24px 70px rgba(0,0,0,0.55)",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.4,
              px: 2,
              py: 1.6,
              borderBottom: `1px solid ${demo.border}`,
              background: "linear-gradient(135deg, rgba(124,58,237,0.20), rgba(167,139,250,0.10))",
            }}
          >
            <Avatar
              sx={{
                width: 38,
                height: 38,
                background: "linear-gradient(135deg, #7c3aed, #a78bfa)",
              }}
            >
              <SmartToyOutlinedIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: demo.text, fontWeight: 700, fontSize: 14.5, lineHeight: 1.1 }}>
                ServoSaathi
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mt: 0.3 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: "#a78bfa", boxShadow: "0 0 6px #a78bfa" }} />
                <Typography sx={{ color: "#a78bfa", fontSize: 11.5, fontWeight: 600 }}>Agentic AI · Online</Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: demo.textDim }}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box
            ref={scrollRef}
            sx={{
              flex: 1,
              overflowY: "auto",
              px: 1.8,
              py: 1.8,
              display: "flex",
              flexDirection: "column",
              gap: 1.3,
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(255,255,255,0.12)",
                borderRadius: "3px",
              },
            }}
          >
            {messages.map((m, i) => {
              const isUser = m.role === "user";
              const showDots = m.streaming && !m.text;
              return (
                <Box
                  key={i}
                  sx={{
                    alignSelf: isUser ? "flex-end" : "flex-start",
                    maxWidth: "84%",
                    px: 1.6,
                    py: 1.1,
                    borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                    background: isUser
                      ? `linear-gradient(135deg, ${demo.primary}, ${demo.primaryDeep})`
                      : demo.surface,
                    border: isUser ? "none" : `1px solid ${demo.border}`,
                    color: isUser ? "#fff" : demo.text,
                    fontSize: 13.5,
                    lineHeight: 1.5,
                    whiteSpace: isUser ? "pre-wrap" : "normal",
                    wordBreak: "break-word",
                  }}
                >
                  {showDots ? <TypingDots /> : isUser ? m.text : <MarkdownLite text={m.text} />}
                </Box>
              );
            })}
          </Box>

          {/* Composer */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              gap: 1,
              p: 1.4,
              borderTop: `1px solid ${demo.border}`,
            }}
          >
            <TextField
              inputRef={inputRef}
              fullWidth
              multiline
              maxRows={4}
              size="small"
              placeholder="Ask the assistant…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(255,255,255,0.05)",
                  borderRadius: "12px",
                  color: demo.text,
                  fontSize: 13.5,
                  "& fieldset": { borderColor: demo.border },
                  "&:hover fieldset": { borderColor: demo.borderStrong },
                  "&.Mui-focused fieldset": { borderColor: demo.primary },
                },
                "& textarea::placeholder": { color: demo.textFaint, opacity: 1 },
              }}
            />
            <IconButton
              onClick={send}
              disabled={busy || !input.trim()}
              sx={{
                width: 40,
                height: 40,
                color: "#fff",
                background: "linear-gradient(135deg, #7c3aed, #a78bfa)",
                "&:hover": { background: "linear-gradient(135deg, #6d28d9, #7c3aed)" },
                "&.Mui-disabled": { background: "rgba(255,255,255,0.08)", color: demo.textFaint },
              }}
            >
              <SendRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Fade>
    </>
  );
};

export default ChatWidget;
